/**
 * Numbers, percentages and amounts in the reader's locale: „1.234,5“,
 * „12,5 %“, „1.234,50 €“.
 *
 * Values may arrive as numbers or as the decimal strings the backend sends
 * for exact amounts (`"1234.50"`). A missing or unreadable value gives `''`
 * (or `fallback`) — never `NaN`, and never „0,00 €“ for a value that is not
 * there. Locale-typed input („1.234,5“) is not parsed here; that is user
 * input and needs a locale-aware parser.
 */
import { numberFormat } from './intlCache';

export type NxNumberLike = number | string | null | undefined;

export interface NxFormatNumberOptions {
  /** Exact number of decimals (sets both minimum and maximum). */
  digits?: number;
  /** At least this many decimals. Defaults to 0. */
  minDigits?: number;
  /** At most this many decimals. Defaults to 2. */
  maxDigits?: number;
  /** Thousands separators; defaults to `true`. */
  grouping?: boolean;
  /** Show a `+` on positive values („+3“), e.g. for deltas. */
  signed?: boolean;
  locale?: string;
  /** Shown for empty or unreadable input. Defaults to `''`. */
  fallback?: string;
}

export interface NxFormatMoneyOptions {
  /** Decimals; defaults to the currency's own (2 for EUR, 0 for JPY). */
  digits?: number;
  signed?: boolean;
  locale?: string;
  fallback?: string;
}

/** A finite number from a number or a plain decimal string, else `null`. */
export function toFiniteNumber(value: NxNumberLike): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value.trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function fractionDigits(
  options: Pick<NxFormatNumberOptions, 'digits' | 'minDigits' | 'maxDigits'>,
  defaultMax: number
): Intl.NumberFormatOptions {
  if (options.digits !== undefined) {
    return { minimumFractionDigits: options.digits, maximumFractionDigits: options.digits };
  }
  const min = options.minDigits ?? 0;
  return {
    minimumFractionDigits: min,
    maximumFractionDigits: Math.max(min, options.maxDigits ?? defaultMax),
  };
}

function signDisplay(signed: boolean | undefined): Intl.NumberFormatOptions {
  return signed ? { signDisplay: 'exceptZero' } : {};
}

/** A plain number: „1.234,5“. Two decimals at most unless told otherwise. */
export function formatNumber(value: NxNumberLike, options: NxFormatNumberOptions = {}): string {
  const n = toFiniteNumber(value);
  if (n === null) return options.fallback ?? '';
  return numberFormat(options.locale, {
    ...fractionDigits(options, 2),
    ...signDisplay(options.signed),
    ...(options.grouping === false ? { useGrouping: false } : {}),
  }).format(n);
}

/**
 * A ratio as a percentage: `0.125` → „12,5 %“. Pass a number for fixed
 * decimals (`formatPercent(0.5, 1)` → „50,0 %“); by default at most one.
 */
export function formatPercent(
  ratio: NxNumberLike,
  digitsOrOptions?: number | NxFormatNumberOptions
): string {
  const options: NxFormatNumberOptions =
    typeof digitsOrOptions === 'number' ? { digits: digitsOrOptions } : (digitsOrOptions ?? {});
  const n = toFiniteNumber(ratio);
  if (n === null) return options.fallback ?? '';
  return numberFormat(options.locale, {
    style: 'percent',
    ...fractionDigits(options, 1),
    ...signDisplay(options.signed),
  }).format(n);
}

/** An amount with its currency: „1.234,50 €“. The currency defaults to EUR. */
export function formatMoney(
  amount: NxNumberLike,
  currency = 'EUR',
  options: NxFormatMoneyOptions = {}
): string {
  const n = toFiniteNumber(amount);
  if (n === null) return options.fallback ?? '';
  return numberFormat(options.locale, {
    style: 'currency',
    currency,
    ...(options.digits !== undefined
      ? { minimumFractionDigits: options.digits, maximumFractionDigits: options.digits }
      : {}),
    ...signDisplay(options.signed),
  }).format(n);
}
